import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createTheme as createAdminTheme } from "@theme/admin/index";
import { createTheme as createClientTheme } from "@theme/client/index";
import useUserPreferencesStore from "@stores/shared/useUserPreferencesStore";
import useTenantContextStore from "@stores/shared/useTenantContextStore";
import { parseThemeValues } from "@theme/shared/theme-values";
import { THEME_KEYS } from "@config/shared/storage-keys";

const ThemeContext = createContext(null);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState("light"); // 'light' or 'dark'
  const [themeType, setThemeType] = useState("client"); // 'admin' or 'client' - default to client
  const [colorPreset, setColorPreset] = useState("orange"); // Default to orange for client theme
  const [contrast, setContrast] = useState("normal"); // 'normal' or 'high'
  const [isDyslexic, setIsDyslexic] = useState(false);
  const [responsiveFontSizes, setResponsiveFontSizes] = useState(true);
  const { setPreference } = useUserPreferencesStore();

  // Tenant branding: fetch the tenant context once at startup and derive the
  // branding colours from its `theme`. The theme re-memoizes when it arrives,
  // so the app switches from Oxygen defaults to tenant colours automatically.
  const tenantTheme = useTenantContextStore((state) => state.theme);
  const branding = useMemo(() => parseThemeValues(tenantTheme), [tenantTheme]);

  useEffect(() => {
    useTenantContextStore.getState().ensureLoaded();
  }, []);

  // Load theme preferences from localStorage ONLY (no API calls)
  // API preferences are loaded separately by ColorMode/FontMode when authenticated
  useEffect(() => {
    const loadThemePreferences = () => {
      const savedType = localStorage.getItem(THEME_KEYS.TYPE);
      const savedColorPreset = localStorage.getItem(THEME_KEYS.COLOR_PRESET);
      const savedContrast = localStorage.getItem(THEME_KEYS.CONTRAST);
      const savedResponsiveFontSizes = localStorage.getItem(
        THEME_KEYS.RESPONSIVE_FONT_SIZES,
      );

      if (savedType) setThemeType(savedType);
      // If no saved color preset, use orange for client and blue for admin
      if (savedColorPreset) {
        setColorPreset(savedColorPreset);
      } else {
        const defaultPreset =
          (savedType || "client") === "client" ? "orange" : "blue";
        setColorPreset(defaultPreset);
      }
      if (savedContrast) setContrast(savedContrast);

      if (savedResponsiveFontSizes)
        setResponsiveFontSizes(savedResponsiveFontSizes === "true");
    };

    loadThemePreferences();
  }, []);

  // Create the theme based on mode and type
  const theme = useMemo(() => {
    const createTheme =
      themeType === "admin" ? createAdminTheme : createClientTheme;

    return createTheme({
      paletteMode: mode,
      colorPreset,
      contrast,
      direction: "ltr",
      isDyslexic,
      responsiveFontSizes,
      branding,
    });
  }, [mode, themeType, colorPreset, contrast, isDyslexic, responsiveFontSizes, branding]);

  const toggleMode = useCallback(async () => {
    const newMode = mode === "light" ? "dark" : "light";
    try {
      await setPreference("color-mode", newMode);
      setMode(newMode);
    } catch (error) {
      console.error("Failed to update theme mode:", error);
    }
  }, [mode, setPreference]);

  const setAdminTheme = useCallback(() => {
    setThemeType("admin");
    localStorage.setItem(THEME_KEYS.TYPE, "admin");
    // Switch to blue for admin theme
    setColorPreset("blue");
    localStorage.setItem(THEME_KEYS.COLOR_PRESET, "blue");
  }, []);

  const setClientTheme = useCallback(() => {
    setThemeType("client");
    localStorage.setItem(THEME_KEYS.TYPE, "client");
    // Switch to orange for client theme
    setColorPreset("orange");
    localStorage.setItem(THEME_KEYS.COLOR_PRESET, "orange");
  }, []);

  const updateColorPreset = useCallback((preset) => {
    setColorPreset(preset);
    localStorage.setItem(THEME_KEYS.COLOR_PRESET, preset);
  }, []);

  const updateContrast = useCallback((contrastValue) => {
    setContrast(contrastValue);
    localStorage.setItem(THEME_KEYS.CONTRAST, contrastValue);
  }, []);

  const toggleDyslexic = useCallback(async () => {
    const newValue = !isDyslexic;
    try {
      await setPreference("enable-dyslexic-font", String(newValue));
      setIsDyslexic(newValue);
    } catch (error) {
      console.error("Failed to update dyslexic mode:", error);
    }
  }, [isDyslexic, setPreference]);

  const toggleResponsiveFontSizes = useCallback(() => {
    const newValue = !responsiveFontSizes;
    setResponsiveFontSizes(newValue);
    localStorage.setItem(THEME_KEYS.RESPONSIVE_FONT_SIZES, String(newValue));
  }, [responsiveFontSizes]);

  // Hydrate mode/isDyslexic from a source outside ThemeProvider (e.g. an
  // authenticated preferences fetch). Does not call the API itself.
  const hydrateMode = useCallback((value) => {
    setMode(value);
  }, []);

  const hydrateIsDyslexic = useCallback((value) => {
    setIsDyslexic(value);
  }, []);

  const value = useMemo(
    () => ({
      mode,
      themeType,
      colorPreset,
      contrast,
      isDyslexic,
      responsiveFontSizes,
      toggleMode,
      setAdminTheme,
      setClientTheme,
      updateColorPreset,
      updateContrast,
      toggleDyslexic,
      toggleResponsiveFontSizes,
      hydrateMode,
      hydrateIsDyslexic,
    }),
    [
      mode,
      themeType,
      colorPreset,
      contrast,
      isDyslexic,
      responsiveFontSizes,
      toggleMode,
      setAdminTheme,
      setClientTheme,
      updateColorPreset,
      updateContrast,
      toggleDyslexic,
      toggleResponsiveFontSizes,
      hydrateMode,
      hydrateIsDyslexic,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
